export interface TokenAmount {
  amount: number;
  currency: string;
}

export function isPointsCurrency(currency: string): boolean {
  return currency === 'POINT' || currency === 'POINTS';
}

export function filterTokenEarnings(earnings: TokenAmount[]): TokenAmount[] {
  return earnings.filter((e) => !isPointsCurrency(e.currency));
}

export type AffiliateStatus = 'Active' | 'Paused' | 'Flagged' | 'Terminated';
export type AffiliateRegion =
  | 'CN'
  | 'JP'
  | 'KR'
  | 'SEA'
  | 'IND'
  | 'EN'
  | 'LATAM'
  | 'CIS'
  | 'GE'
  | 'SP'
  | 'SA'
  | 'EU'
  | 'Other';

export const STATUS_DISPLAY: Record<
  AffiliateStatus,
  { label: string; variant: 'success' | 'warning' | 'error' | 'default' }
> = {
  Active: { label: 'Active', variant: 'success' },
  Paused: { label: 'Paused', variant: 'warning' },
  Terminated: { label: 'Terminated', variant: 'error' },
  Flagged: { label: 'Flagged', variant: 'default' },
};

export const REGION_OPTIONS: AffiliateRegion[] = [
  'CN',
  'JP',
  'KR',
  'SEA',
  'IND',
  'EN',
  'LATAM',
  'CIS',
  'GE',
  'SP',
  'SA',
  'EU',
  'Other',
];

export type AffiliateTaxStatus = 'not_required' | 'not_submitted' | 'submitted' | 'approved' | 'revoked' | 'expired';

export const TAX_STATUS_DISPLAY: Record<
  AffiliateTaxStatus,
  { label: string; variant: 'success' | 'warning' | 'error' | 'default' | 'outline' }
> = {
  not_required: { label: '—', variant: 'outline' },
  not_submitted: { label: 'Not submitted', variant: 'error' },
  submitted: { label: 'Submitted', variant: 'warning' },
  approved: { label: 'Approved', variant: 'success' },
  revoked: { label: 'Revoked', variant: 'error' },
  expired: { label: 'Expired', variant: 'warning' },
};

/** Filter options shown in the UI (excludes `not_required` which only appears when tax info isn't enabled). */
export const TAX_STATUS_FILTER_OPTIONS: { value: AffiliateTaxStatus; label: string }[] = [
  { value: 'not_submitted', label: 'Not submitted' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'expired', label: 'Expired' },
  { value: 'revoked', label: 'Revoked' },
];

/**
 * Derived on read from the tier-protection expiry — there is no stored state. `null` is broader than
 * "no tier protection": it also covers a protection that lapsed more than 7 days ago (`expired`
 * decays deliberately), one pending manual approval, one ranking below the affiliate's effective
 * tier, and a soft-deleted protection tier.
 *
 * Independent of `TierProtection[]` — an affiliate can carry one without the other. Never derive
 * one from the other.
 */
export const PROTECTION_STATUS_VALUES = ['protected', 'expiring', 'expired'] as const;
export type ProtectionStatus = (typeof PROTECTION_STATUS_VALUES)[number];

export const PROTECTION_STATUS_DISPLAY: Record<
  ProtectionStatus,
  { label: string; variant: 'success' | 'warning' | 'error' }
> = {
  protected: { label: 'Protected', variant: 'success' },
  expiring: { label: 'Expiring', variant: 'warning' },
  expired: { label: 'Expired', variant: 'error' },
};

export const PROTECTION_STATUS_FILTER_OPTIONS: { value: ProtectionStatus; label: string }[] =
  PROTECTION_STATUS_VALUES.map((value) => ({
    value,
    label: PROTECTION_STATUS_DISPLAY[value].label,
  }));

export interface OrganicTier {
  id: string;
  name: string;
  slug: string;
  rank: number;
}

export interface TierProtection {
  tier_id: string;
  tier_name: string;
  tier_rank: number;
  ends_at: string;
  created_at: string;
  is_below_effective: boolean;
}

export interface AffiliateListItem {
  project_affiliate_id: string;
  alias: string | null;
  user_identifier: string;
  user_identifier_type: string;
  referred_volume: number;
  r2_volume: number;
  r3_volume: number;
  multilevel_volume: number;
  total_volume: number;
  referred_revenue: number;
  referred_attributions: number;
  total_earnings: TokenAmount[];
  referred_users: number;
  status: AffiliateStatus;
  first_seen: string | null;
  region: AffiliateRegion;
  tax_status: AffiliateTaxStatus;
  audiences: { id: string; name: string }[];
  referral_codes: string[];
  organic_tier: OrganicTier | null;
  current_tier: OrganicTier | null;
  protections: TierProtection[];
  protection_status: ProtectionStatus | null;
  /** Expiry backing `protection_status`. Non-null whenever that is non-null. */
  protection_expires_at: string | null;
}

export interface AffiliateListResponse {
  total_count: number;
  results: AffiliateListItem[];
}

export interface AffiliateDetail {
  id: string;
  user_identifier: string;
  user_identifier_type: string;
  alias: string | null;
  status: AffiliateStatus;
  region: AffiliateRegion;
  note: string | null;
  audiences?: { id: string; name: string }[];
  referral_codes?: string[];
  organic_tier: OrganicTier | null;
  current_tier: OrganicTier | null;
  protections: TierProtection[];
  protection_status: ProtectionStatus | null;
  /** Expiry backing `protection_status`. Non-null whenever that is non-null. */
  protection_expires_at: string | null;
}

export interface SetTierProtectionDto {
  tier_id: string;
  expires_at: string;
}

export interface CreateAffiliateDto {
  user_identifier: string;
  user_identifier_type: 'evm_address' | 'solana_address' | 'sui_address' | 'xrpl_address' | 'stellar_address' | 'email';
  alias?: string;
  region?: AffiliateRegion;
  status?: AffiliateStatus;
  /** Must be sent together with `tier_protection_days` or both omitted. */
  tier_protection_tier_id?: string;
  /** Must be sent together with `tier_protection_tier_id`. Range: 1–365. */
  tier_protection_days?: number;
  note?: string;
}

export interface UpdateAffiliateDto {
  audiences?: string[];
  region?: AffiliateRegion;
  status?: AffiliateStatus;
  alias?: string;
  note?: string;
}

export interface AffiliateStats {
  user_identifier: string;
  user_identifier_type: string;
  total_earnings: TokenAmount[];
  referred_volume: number;
  r2_volume: number;
  r3_volume: number;
  r4_volume: number;
  multilevel_volume: number;
  total_volume: number;
  referred_revenue: number;
  r2_revenue: number;
  r3_revenue: number;
  r4_revenue: number;
  referred_users: number;
  status: AffiliateStatus;
  region: AffiliateRegion;
  referral_codes: string[];
}

export interface TotalStats {
  total_affiliates: number;
  active_affiliates: number;
  suspended_affiliates: number;
  total_regions: number;
  total_volume: number;
  total_revenue: number;
  total_attributions: number;
  total_referred_users: number;
  total_earnings: TokenAmount[];
  total_points_paid: number;
}

export interface StatsBreakdownEntry {
  date: string;
  direct_volume: number;
  indirect_volume: number;
  direct_revenue: number;
  indirect_revenue: number;
  attributions: number;
  referred_users: number;
  earnings: number;
  earnings_currency: string | null;
}

/** Granularity options exposed to the user. `'all'` is resolved client-side. */
export type BreakdownGranularity = 'day' | 'week' | 'month' | 'all';
/** Granularities accepted by the backend `stats-breakdown` endpoint. */
export type BreakdownGroupBy = 'day' | 'week' | 'month';

export interface GlobalBreakdownResult {
  groupKey: string;
  affiliates: number;
  r1Volume: number;
  indirectVolume: number;
  totalReferralVolume: number;
  revenueFromReferrals: number;
  earnings: TokenAmount[];
  pointsPaid: number;
}

export interface GlobalBreakdownResponse {
  groupBy: string;
  totalGroups: number;
  totalVolume: number;
  totalRevenue: number;
  totalEarnings: TokenAmount[];
  totalPointsPaid: number;
  results: GlobalBreakdownResult[];
}

export interface ReferralTreeNode {
  user_identifier: string;
  project_affiliate_id: string | null;
  users: number;
  /** @deprecated Same as `users`; kept for older API responses */
  traders?: number;
  /** Volume attributed to this node as referrer (direct referrals). */
  referrer_volume: number;
  /** Volume where this node is the attributed end user. */
  total_volume: number;
  total_revenue: number;
  children: ReferralTreeNode[];
}

export interface PayoutRate {
  amount: string | null;
  currency: string | null;
  unit: string;
}

export interface AudiencePayoutTerm {
  conversion_name: string;
  payout_type: 'variable' | 'fixed';
  payee_type: 'affiliate' | 'end-user' | 'both';
  is_multi_level: boolean;
  affiliate_rate: PayoutRate;
  affiliate2_rate: PayoutRate;
  affiliate3_rate: PayoutRate;
  end_user_rate: PayoutRate;
}

export interface AudiencePayoutTerms {
  audience_id: string | null;
  audience_name: string | null;
  payout_terms: AudiencePayoutTerm[];
}

export interface PayoutTermsByAudienceResponse {
  audiences: AudiencePayoutTerms[];
}

export interface TierPayoutTerm {
  conversion_name: string;
  payout_type: 'variable' | 'fixed';
  payee_type: 'affiliate' | 'end-user' | 'both';
  is_multi_level: boolean;
  affiliate_rate: PayoutRate;
  affiliate2_rate: PayoutRate;
  affiliate3_rate: PayoutRate;
  affiliate4_rate: PayoutRate;
  end_user_rate: PayoutRate;
}

export interface TierPayoutTerms {
  tier_id: string | null;
  tier_name: string | null;
  tier_description: string | null;
  tier_slug: string | null;
  tier_rank: number | null;
  payout_terms: TierPayoutTerm[];
}

export interface PayoutTermsByTierResponse {
  tiers: TierPayoutTerms[];
}

export type AffiliateSortField =
  | 'r1_volume'
  | 'total_volume'
  | 'revenue'
  | 'earnings'
  | 'referred_attributions'
  | 'referred_users';

export type DateRangePreset = '7d' | '30d' | '90d' | 'MTD' | 'QTD' | 'custom' | 'all';

export interface DateRangeParams {
  /** Preset key. `'all'` or omitted means lifetime (unchanged from before PR #2737). */
  dateRange?: DateRangePreset;
  /** ISO 8601 date string. Required when `dateRange === 'custom'`, ignored otherwise. */
  dateFrom?: string;
  /** ISO 8601 date string. Required when `dateRange === 'custom'`, ignored otherwise. */
  dateTo?: string;
}

export interface AffiliateListParams extends DateRangeParams {
  page?: number;
  per_page?: number;
  sort_by?: AffiliateSortField;
  sort_order?: 'asc' | 'desc';
  query?: string;
  statuses?: string[];
  regions?: string[];
  tiers?: string[];
  tax_statuses?: AffiliateTaxStatus[];
  protection_statuses?: ProtectionStatus[];
}

export interface AffiliateTaxInfoDetail {
  id: string;
  user_identifier: string;
  user_identifier_type: string;
  form_type: 'w9' | 'w8ben' | 'w8bene';
  legal_name: string;
  business_name: string | null;
  tax_classification: string | null;
  country: string;
  address_line1: string;
  address_line2: string | null;
  city: string;
  state: string | null;
  zip: string;
  tax_id_number_masked: string | null;
  foreign_tax_id_masked: string | null;
  date_of_birth: string | null;
  treaty_country: string | null;
  treaty_article: string | null;
  entity_type: string | null;
  country_of_incorporation: string | null;
  country_of_citizenship: string | null;
  signature_date: string;
  status: 'submitted' | 'approved' | 'revoked' | 'expired';
  revoked_reason: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface RevokeAffiliateTaxInfoDto {
  revoked_reason: string;
}

export interface AffiliateStatsParams {
  user_identifier: string;
  from?: string;
  to?: string;
}

export interface StatsBreakdownParams {
  user_identifier: string;
  group_by: BreakdownGroupBy;
  from?: string;
  to?: string;
}

export interface GlobalBreakdownParams {
  groupBy: string;
  dateRange?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortOrder?: string;
  statuses?: string[];
  regions?: string[];
  tiers?: string[];
}
